import './richiesta-ferie.scss';

import React, { useRef, useState, useCallback, useEffect } from "react";
import { confirm } from 'devextreme/ui/dialog';
import { ToastType } from 'devextreme/ui/toast';
import { DataGrid, DropDownButton, Button, Toast, SpeedDialAction } from 'devextreme-react';
import { Column, Toolbar, DataGridRef, DataGridTypes, Grouping, Sorting, Selection, Scrolling } from 'devextreme-react/data-grid';
import { Item } from "devextreme-react/toolbar";
import useApiConfiguration from '../../apiConfig';
import { useScreenSize, useScreenSizeClass } from '../../utils/media-query';
import config from "devextreme/core/config";
import { useRichiestaFerieDataSource } from '../../datasources/richiestaFerieDataSource';
import { RichiesteFeriePopup, RichiesteFeriePopupEdit } from "../../components";
import { PopupRichiestaFerieHandle } from '../../components/popup-richiesta-ferie/popupRichiestaFerie';
import { PopupEditRichiestaFerieHandle } from '../../components/popup-richiesta-ferie/popupEditRichiestaFerie';
import { RichiesteCausaliCDLApi } from '../../data';

export default function RichiestaFerie() {
    const gridRef = useRef<DataGridRef>(null);
    const popupRef = useRef<PopupRichiestaFerieHandle>(null);
    const popupEditRef = useRef<PopupEditRichiestaFerieHandle>(null);

    const apiConfiguration = useApiConfiguration();
    const { isLarge, isMedium } = useScreenSize();
    const screenSizeClass = useScreenSizeClass();

    const [toastConfig, setToastConfig] = useState({ isVisible: false, type: "success", message: '' } as { isVisible: boolean, type: ToastType, message: string });
    const [selectedIdRichiesta, setSelectedIdRichiesta] = useState<number>(-1);
    const [canDeleteSelRichiesta, setCanDeleteSelRichiesta] = useState<boolean>(false);

    config({
        floatingActionButtonConfig: {
            icon: "optionsgear",
            shading: true,
            position: {
                my: "right bottom",
                at: "right bottom",
                of: "#gridRichiesteFerie",
                offset: "-16 -16"
            }
        }
    });

    function formatDate(date: Date): string {
        return date.toLocaleDateString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
        });
    };

    function handleGridRowClick(e: DataGridTypes.RowClickEvent) {
        e.event.preventDefault();
        e.event.stopPropagation();

        const idRichiesta = e.data?.id;
        if (idRichiesta == null) return;

        if (idRichiesta === selectedIdRichiesta) {
            setSelectedIdRichiesta(-1);
            setCanDeleteSelRichiesta(false);
            gridRef.current?.instance().deselectAll();
        } else {
            setSelectedIdRichiesta(idRichiesta);
            setCanDeleteSelRichiesta(e.data?.isDeletable);
        }
    };

    function newRichiestaFerie(giorni: string) {
        popupRef.current?.show(giorni);
    };

    function openDetails(id: number) {
        popupEditRef.current?.show(id, false);
    };

    function checkDelete(id: number) {
        const result = confirm("Confermi l'eliminazione?", "Conferma");
        result.then((dialogRes) => {
            if (dialogRes)
                deleteRichiestaFerie(id);
        });
    };

    function deleteRichiestaFerie(id: number) {
        var api = new RichiesteCausaliCDLApi(apiConfiguration);
        api.deleteRichiestaCausaleCdl({
            id: id
        }).then(() => {
            refresh();
            setToastConfig({
                ...toastConfig,
                isVisible: true,
                type: "success",
                message: 'Richiesta eliminata con successo'
            });
        }).catch((error) => {
            console.error(error);
            setToastConfig({
                ...toastConfig,
                isVisible: true,
                type: "error",
                message: 'Errore durante l\'eliminazione della richiesta'
            });
        });
    };

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false
        });
    }, []);

    const refresh = useCallback(() => {
        gridRef.current?.instance().refresh();
    }, []);

    useEffect(() => {
        gridRef.current?.instance().deselectAll();
        setSelectedIdRichiesta(-1);
        setCanDeleteSelRichiesta(false);
    }, [isLarge, isMedium]);

    return (
        <React.Fragment>
            <div className='view-list-richiesta-ferie'>
                <div className={`view-wrapper view-wrapper-list list-page ${screenSizeClass}`}>
                    <DataGrid
                        id="gridRichiestaFerie" ref={gridRef} key="id"
                        dataSource={useRichiestaFerieDataSource()}
                        showBorders rowAlternationEnabled
                        noDataText='Nessuna richiesta caricata'
                        className='grid theme-dependent'
                        defaultFocusedRowIndex={0} onRowClick={handleGridRowClick}
                    >
                        <Sorting mode="single" />
                        <Grouping autoExpandAll={false} expandMode='rowClick' />
                        <Scrolling mode="virtual" />
                        {!isLarge && !isMedium && <Selection mode="single" />}

                        <Toolbar>
                            <Item location="before">
                                <div className='grid-header'>Storico Richieste</div>
                            </Item>

                            <Item location='after'
                                locateInMenu='auto'
                                showText='inMenu'
                                widget='dxDropDownButton'
                            >
                                <DropDownButton icon="add" stylingMode='text' visible={isLarge || isMedium}
                                    dropDownOptions={{ width: 230 }} showArrowIcon={false}
                                    items={[
                                        { id: 'S', text: 'Giorno singolo' },
                                        { id: 'M', text: 'Giorni multipli' }
                                    ]}
                                    onItemClick={(e) => newRichiestaFerie(e.itemData.id)}
                                />
                            </Item>

                            <Item
                                location='after'
                                locateInMenu='never'
                                showText='inMenu'
                                widget='dxButton'
                            >
                                <Button
                                    icon='refresh'
                                    text='Refresh'
                                    stylingMode='text'
                                    onClick={refresh}
                                />
                            </Item>
                        </Toolbar>

                        <Column
                            type='buttons'
                            visible={isLarge || isMedium}
                            width={90}
                            cellRender={(cellData) => (
                                <div style={{ display: "flex" }}>
                                    <Button icon="search" hint="Dettagli" stylingMode='text' onClick={() => openDetails(cellData.data.id)} />
                                    <Button icon="trash" hint="Elimina" stylingMode='text' visible={cellData.data?.isDeletable ?? false} onClick={() => checkDelete(cellData.data.id)} />
                                </div>
                            )}
                        />
                        <Column dataField='dataRichiesta' dataType='date' hidingPriority={2} />
                        <Column dataField='causale' hidingPriority={1} />
                        <Column
                            caption='Tempistiche'
                            cellRender={({ data }) => {
                                if (data.ore != null) {
                                    if (data.ore < 0)
                                        return `Storno di ${data.ore * -1} ${data.ore < -1 ? "ore" : "ora"} il ${formatDate(data.daData)}`;

                                    return `${data.ore} ${data.ore > 1 ? "ore" : "ora"} il ${formatDate(data.daData)}`;
                                } else
                                    return `Dal ${formatDate(data.daData)} al ${formatDate(data.aData)}`;
                            }}
                            hidingPriority={3}
                        />
                        <Column
                            dataField='approvazione' caption='Stato Richiesta' groupIndex={0}
                            sortOrder='asc'
                            sortingMethod={(a, b) => {
                                const order = ["In approvazione"];
                                const iA = order.indexOf(a);
                                const iB = order.indexOf(b);

                                // se non esiste nell'array, lo metti in fondo
                                return (iA === -1 ? 99 : iA) - (iB === -1 ? 99 : iB);
                            }}
                        />
                        <Column dataField='approvatore' hidingPriority={0} />
                    </DataGrid>

                    <SpeedDialAction
                        icon="add" label="Giorno singolo"
                        visible={!isLarge && !isMedium && selectedIdRichiesta === -1}
                        index={1} onClick={() => newRichiestaFerie('S')}
                    />
                    <SpeedDialAction
                        icon="add" label="Giorni multipli"
                        visible={!isLarge && !isMedium && selectedIdRichiesta === -1}
                        index={2} onClick={() => newRichiestaFerie('M')}
                    />
                    <SpeedDialAction
                        icon="search" label="Dettagli"
                        visible={!isLarge && !isMedium && selectedIdRichiesta > 0}
                        index={3} onClick={() => openDetails(selectedIdRichiesta)}
                    />
                    <SpeedDialAction
                        icon="trash" label="Elimina"
                        visible={!isLarge && !isMedium && selectedIdRichiesta > 0 && canDeleteSelRichiesta}
                        index={4} onClick={() => checkDelete(selectedIdRichiesta)}
                    />
                </div>
            </div>

            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                type={toastConfig.type}
                position={{ at: 'right bottom', my: 'right bottom', of: window, offset: { x: -20, y: -20 } }}
                height={45} width={200}
                onHiding={onHidingToast}
                displayTime={3000}
                animation={{ show: { type: 'fade', duration: 400, from: 0, to: 1 }, hide: { type: 'fade', duration: 40, to: 0 } }}
            />

            <RichiesteFeriePopup ref={popupRef} onChange={refresh} />
            <RichiesteFeriePopupEdit ref={popupEditRef} onChange={refresh} />
        </React.Fragment>
    );
}